import { useMemo } from "react";
import { UIPrice } from "@flights/types";
import { StackProps, TextProps } from "@bookingcom/bui-react";
import useFormatPrice from "hooks/useFormatPrice";
import useUserAgent from "hooks/useUserAgent";
import { pricingUtils } from "@flights/web-api-utils-universal";
import { type UnifiedPriceTagPriceBreakdownProps } from "./UnifiedPriceTagPriceBreakdown";

export type UnifiedPriceTagAlignment = "start" | "end";

export type UnifiedPriceTagPosition = "top" | "bottom" | UnifiedPriceTagAlignment;

export type UnifiedPriceTagControllerProps = {
  price: UIPrice;
  priceProps?: Pick<TextProps, "attributes" | "tagName" | "variant">;
  priceAlignment?: UnifiedPriceTagAlignment;
  priceTranslations?: ((formattedPrice: string) => string)[];

  pricePerAdult?: UIPrice;
  pricePerAdultTranslations?: ((formattedPrice: string) => string)[];
  pricePerAdultExplanationTranslations?: ((formattedPrice: string) => string)[];
  pricePerAdultExplanationProps?: Pick<TextProps, "variant" | "attributes">;
  shouldShowPricePerAdult?: boolean;

  strikethroughPrice?: UIPrice;
  strikethroughPriceProps?: Pick<TextProps, "variant">;
  strikethroughPricePosition?: UnifiedPriceTagPosition;

  priceExplanation?: string;
  priceExplanationProps?: Pick<TextProps, "attributes">;

  shouldRoundPrice?: boolean;
} & UnifiedPriceTagPriceBreakdownProps;

export const useUnifiedPriceTagController = (props: UnifiedPriceTagControllerProps) => {
  const { isMobile } = useUserAgent();
  const { formatPrice } = useFormatPrice();

  const {
    price,
    priceAlignment,
    priceTranslations = [],
    pricePerAdult,
    pricePerAdultExplanationTranslations = [],
    pricePerAdultTranslations = [],
    priceProps,
    shouldRoundPrice,
    shouldShowPricePerAdult = false,
    strikethroughPrice,
    strikethroughPricePosition = "top"
  } = props;

  const formattedPrice = useMemo((): string => {
    const priceValue = shouldShowPricePerAdult && pricePerAdult ? pricePerAdult : price;
    const priceFormatted = formatPrice(priceValue, shouldRoundPrice ? { decimalPlaces: 0 } : {});

    return (shouldShowPricePerAdult && pricePerAdult ? pricePerAdultTranslations : priceTranslations).reduce(
      (acc, translate) => translate(acc),
      priceFormatted
    );
  }, [
    price,
    pricePerAdult,
    priceTranslations,
    pricePerAdultTranslations,
    shouldShowPricePerAdult,
    shouldRoundPrice,
    formatPrice
  ]);

  const formattedStrikethroughPrice = useMemo((): string => {
    return !!strikethroughPrice ? formatPrice(strikethroughPrice, shouldRoundPrice ? { decimalPlaces: 0 } : {}) : "";
  }, [formatPrice, shouldRoundPrice, strikethroughPrice]);

  const priceWrapperStackProps = useMemo(
    (): StackProps => ({
      justifyContent: priceAlignment === "end" ? priceAlignment : "start"
    }),
    [priceAlignment]
  );

  const priceStackProps = useMemo((): StackProps => {
    if (!strikethroughPrice) {
      return {};
    }

    const direction: StackProps["direction"] =
      strikethroughPricePosition === "top"
        ? "column"
        : strikethroughPricePosition === "bottom"
        ? "column-reverse"
        : strikethroughPricePosition === "start"
        ? "row"
        : "row-reverse";
    const alignItems = ["top", "bottom"].includes(strikethroughPricePosition) ? priceAlignment : "baseline";
    const gap = ["top", "bottom"].includes(strikethroughPricePosition) ? 0 : 1;

    return { ...priceProps, direction, alignItems, gap };
  }, [priceAlignment, priceProps, strikethroughPrice, strikethroughPricePosition]);

  const priceTextProps = useMemo(
    (): TextProps => ({
      variant: priceProps?.variant || "strong_1",
      tagName: priceProps?.tagName || "div"
    }),
    [priceProps]
  );

  const pricePerAdultExplanation = useMemo((): string => {
    const priceFormatted = formatPrice(price, shouldRoundPrice ? { decimalPlaces: 0 } : {});

    return pricePerAdultExplanationTranslations.reduce((acc, translate) => translate(acc), priceFormatted);
  }, [formatPrice, price, pricePerAdultExplanationTranslations, shouldRoundPrice]);

  const pricePerAdultExplanationStrikethroughInline = useMemo(
    (): boolean => !(isMobile && pricingUtils.isPriceWithLargeNumber(price)),
    [isMobile, price]
  );

  return {
    formattedPrice,
    formattedStrikethroughPrice,
    priceWrapperStackProps,
    priceStackProps,
    priceTextProps,
    pricePerAdultExplanation,
    pricePerAdultExplanationStrikethroughInline
  };
};
